import { useEffect, useRef, useState } from "react";

export type AppFeedbackKind = "info" | "success" | "warning" | "error";

type AppFeedbackPayload = {
  kind: AppFeedbackKind;
  message: string;
  title?: string;
  durationMs?: number;
};

const EVENT_NAME = "mundely:feedback";

export function showAppFeedback(kind: AppFeedbackKind, message: string, title?: string, durationMs?: number): void {
  if (typeof window === "undefined" || !message.trim()) return;
  window.dispatchEvent(new CustomEvent<AppFeedbackPayload>(EVENT_NAME, {
    detail: { kind, message: message.trim(), title, durationMs }
  }));
}

function kindForElement(element: Element): AppFeedbackKind | null {
  if (element.matches(".settings-success,.success-banner,.share-export-success,.useful-info-message.success")) return "success";
  if (element.matches(".settings-warning,.warning-banner,.useful-info-message.warning")) return "warning";
  if (element.matches(".settings-error,.error-banner,.useful-info-message.error")) return "error";
  if (element.matches(".info-note,.trip-inline-info-bubble")) return "info";
  return null;
}

function findFeedbackNodes(root: ParentNode): Element[] {
  const selector = ".settings-success,.success-banner,.share-export-success,.useful-info-message.success,.settings-warning,.warning-banner,.useful-info-message.warning,.settings-error,.error-banner,.useful-info-message.error";
  const items: Element[] = [];
  if (root instanceof Element && root.matches(selector)) items.push(root);
  root.querySelectorAll?.(selector).forEach((item) => items.push(item));
  return items;
}

export function AppFeedbackHost() {
  const [feedback, setFeedback] = useState<(AppFeedbackPayload & { id: number }) | null>(null);
  const timerRef = useRef<number | null>(null);
  const idRef = useRef(0);
  const lastRef = useRef<{ signature: string; at: number }>({ signature: "", at: 0 });

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = null;
    };

    const present = (payload: AppFeedbackPayload) => {
      const message = String(payload.message || "").trim();
      if (!message) return;
      const signature = `${payload.kind}|${payload.title || ""}|${message}`;
      const now = Date.now();
      if (lastRef.current.signature === signature && now - lastRef.current.at < 700) return;
      lastRef.current = { signature, at: now };
      clearTimer();
      idRef.current += 1;
      setFeedback({ ...payload, message, id: idRef.current });
      const duration = payload.durationMs ?? (payload.kind === "error" ? 4300 : payload.kind === "warning" ? 3900 : payload.kind === "info" ? 3600 : 2800);
      timerRef.current = window.setTimeout(() => setFeedback(null), duration);
    };

    const onFeedback = (event: Event) => {
      const detail = (event as CustomEvent<AppFeedbackPayload>).detail;
      if (!detail?.kind || !detail?.message) return;
      present(detail);
    };

    const forwardNode = (element: Element) => {
      if (element.closest("[data-feedback-local='true']")) return;
      const kind = kindForElement(element);
      if (!kind) return;
      const text = String(element.textContent || "").replace(/\s+/g, " ").trim();
      if (!text) return;
      const signature = `${kind}|${text}`;
      if ((element as HTMLElement).dataset.mundelyFeedbackForwarded === signature) return;
      (element as HTMLElement).dataset.mundelyFeedbackForwarded = signature;
      element.classList.add("app-feedback-source-hidden");
      present({ kind, message: text });
    };

    const scan = (root: ParentNode) => findFeedbackNodes(root).forEach(forwardNode);
    scan(document.body);

    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === "characterData") {
          const parent = record.target.parentElement;
          if (parent) scan(parent);
          return;
        }
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) scan(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    window.addEventListener(EVENT_NAME, onFeedback as EventListener);
    return () => {
      clearTimer();
      observer.disconnect();
      window.removeEventListener(EVENT_NAME, onFeedback as EventListener);
    };
  }, []);

  if (!feedback) return null;
  const icon = feedback.kind === "success" ? "✓" : feedback.kind === "warning" ? "!" : feedback.kind === "error" ? "!" : "i";
  return (
    <div className={`app-feedback-popup ${feedback.kind}`} role={feedback.kind === "error" ? "alert" : "status"} aria-live="polite">
      <span className="app-feedback-icon" aria-hidden="true">{icon}</span>
      <div className="app-feedback-copy">
        {feedback.title ? <strong>{feedback.title}</strong> : null}
        <p>{feedback.message}</p>
      </div>
      <button type="button" className="app-feedback-close" onClick={() => setFeedback(null)} aria-label="×">×</button>
    </div>
  );
}
